import { z } from 'zod';
import '@noble/hashes/sha256';

// src/catalog/model.ts

// src/color/index.ts
var CATALOG_PALETTE = [
  { name: "Slate", hex: "#64748b" },
  { name: "Red", hex: "#ef4444" },
  { name: "Orange", hex: "#f97316" },
  { name: "Amber", hex: "#f59e0b" },
  { name: "Yellow", hex: "#eab308" },
  { name: "Lime", hex: "#84cc16" },
  { name: "Green", hex: "#22c55e" },
  { name: "Emerald", hex: "#10b981" },
  { name: "Teal", hex: "#14b8a6" },
  { name: "Cyan", hex: "#06b6d4" },
  { name: "Sky", hex: "#0ea5e9" },
  { name: "Blue", hex: "#3b82f6" },
  { name: "Indigo", hex: "#6366f1" },
  { name: "Violet", hex: "#8b5cf6" },
  { name: "Purple", hex: "#a855f7" },
  { name: "Fuchsia", hex: "#d946ef" },
  { name: "Pink", hex: "#ec4899" },
  { name: "Rose", hex: "#f43f5e" }
];
CATALOG_PALETTE.map((s) => s.hex);
var HEX_RE = /^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;
function isHexColor(value) {
  return typeof value === "string" && HEX_RE.test(value.trim());
}

// src/catalog/entityTypes.ts
var ENTITY_TYPES = [
  "bridge",
  "cefi",
  "company",
  "contract",
  "dao",
  "defi",
  "dex",
  "dex_pool",
  "exchange",
  "individual",
  "mixer",
  "nft",
  "sanctioned",
  "token",
  "wallet",
  "other"
];
var DEFAULT_ENTITY_TYPE = "other";
var ENTITY_TYPE_LABEL = Object.freeze({
  bridge: "Bridge",
  cefi: "Centralised service",
  company: "Company",
  contract: "Contract",
  dao: "DAO",
  defi: "DeFi protocol",
  dex: "DEX",
  dex_pool: "DEX pool",
  exchange: "Exchange",
  individual: "Individual",
  mixer: "Mixer",
  nft: "NFT",
  sanctioned: "Sanctioned",
  token: "Token",
  wallet: "Wallet",
  other: "Other"
});
var KNOWN = new Set(ENTITY_TYPES);
function isEntityType(value) {
  return typeof value === "string" && KNOWN.has(value);
}
function entityTypeLabel(value) {
  if (!value) return "";
  return isEntityType(value) ? ENTITY_TYPE_LABEL[value] : value;
}
function entityTypeOptions(current) {
  const options = ENTITY_TYPES.map((value) => ({
    value,
    label: ENTITY_TYPE_LABEL[value]
  }));
  if (current && !isEntityType(current)) {
    options.push({ value: current, label: `${current} (not a known type)`, disabled: true });
  }
  return options;
}

// src/catalog/model.ts
var CatalogItemKind = {
  TAG: "tag",
  LABEL: "label",
  ENTITY: "entity",
  GROUP: "group",
  ADDRESS: "address"
};
var CATALOG_ITEM_KINDS = Object.values(CatalogItemKind);
var CATALOG_ITEM_KIND_LABEL = {
  tag: "Tag",
  label: "Label",
  entity: "Entity",
  group: "Group",
  address: "Address"
};
var CATALOG_LIFECYCLES = ["verified", "candidate", "deprecated", "quarantined"];
var CATALOG_LIFECYCLE_LABEL = {
  verified: "Verified",
  candidate: "Candidate",
  deprecated: "Deprecated",
  quarantined: "Quarantined"
};
var lifecycleSchema = z.enum(CATALOG_LIFECYCLES);
var severitySchema = z.enum(["info", "low", "medium", "high", "critical"]);
var riskLevelSchema = z.enum(["safe", "low", "medium", "high", "severe"]);
var hexColor = z.string().trim().refine((v) => v === "" || isHexColor(v), { message: "Enter a valid hex colour (e.g. #3b82f6)" });
var baseShape = {
  name: z.string().trim().min(1, "Name is required").max(120, "Name is too long"),
  description: z.string().trim().max(2e3).optional(),
  /** Hex colour for chips/avatars. */
  color: hexColor.optional(),
  /** Logo URI / S3 key (set by the upload flow). */
  logo: z.string().trim().optional(),
  aliases: z.array(z.string().trim().min(1)).optional(),
  /**
   * Chains this row applies to (hex chain id or slug). Empty / omitted = all
   * chains (chain-agnostic). Persisted as `CatalogAssignment` rows by the
   * `@authe/catalog` data layer.
   */
  chains: z.array(z.string().trim().min(1)).optional(),
  /**
   * Chains this row is *primarily / canonically* used on (hex chain id or
   * slug). A presentation hint persisted on the row itself — distinct from
   * `chains` (applicability join). Empty / omitted = none flagged.
   */
  primaryChains: z.array(z.string().trim().min(1)).optional(),
  lifecycle: lifecycleSchema.optional(),
  severity: severitySchema.optional(),
  riskLevel: riskLevelSchema.optional()
};
var tagSchema = z.object({
  ...baseShape,
  kind: z.literal("tag"),
  category: z.string().trim().max(80).optional()
});
var labelSchema = z.object({
  ...baseShape,
  kind: z.literal("label"),
  /** Address-bound label (optional — a label can be global). */
  address: z.string().trim().optional(),
  /** Chain for the bound address. */
  chain: z.string().trim().optional()
});
var entitySchema = z.object({
  ...baseShape,
  kind: z.literal("entity"),
  // The backend's `EntityType` enum, not free text — see `./entityTypes`.
  entityType: z.enum(ENTITY_TYPES, { message: "Pick one of the listed types" }).optional(),
  website: z.string().trim().url("Enter a valid URL").optional().or(z.literal("")),
  twitter: z.string().trim().max(80).optional()
});
var groupSchema = z.object({
  ...baseShape,
  kind: z.literal("group"),
  memberTagIds: z.array(z.string()).optional(),
  memberLabelIds: z.array(z.string()).optional(),
  memberEntityIds: z.array(z.string()).optional()
});
var addressSchema = z.object({
  ...baseShape,
  kind: z.literal("address"),
  address: z.string().trim().min(1, "Address is required"),
  chain: z.string().trim().min(1, "Chain is required"),
  primaryEntityId: z.string().trim().optional()
});
var CatalogItemDraftSchema = z.discriminatedUnion("kind", [
  tagSchema,
  labelSchema,
  entitySchema,
  groupSchema,
  addressSchema
]);
function emptyDraft(kind) {
  const base = { name: "" };
  switch (kind) {
    case "tag":
      return { kind, ...base };
    case "label":
      return { kind, ...base };
    case "entity":
      return { kind, ...base, entityType: DEFAULT_ENTITY_TYPE };
    case "group":
      return { kind, ...base };
    case "address":
      return { kind, ...base, address: "", chain: "" };
  }
}
function validateDraft(draft) {
  const result = CatalogItemDraftSchema.safeParse(draft);
  if (!result.success) {
    const flat = result.error.flatten();
    return {
      success: false,
      fieldErrors: flat.fieldErrors,
      message: flat.formErrors[0]
    };
  }
  const stray = primaryChainsOutsideAppliesTo(result.data);
  if (stray.length > 0) {
    const noun = stray.length === 1 ? "chain is" : "chains are";
    return {
      success: false,
      fieldErrors: {
        primaryChains: [
          `Primary ${noun} not in \u201CApplies to chains\u201D: ${stray.join(", ")}. Add them to \u201CApplies to chains\u201D or remove them from \u201CPrimary chains\u201D.`
        ]
      },
      message: "Primary chains must be within \u201CApplies to chains\u201D."
    };
  }
  return { success: true, data: result.data, fieldErrors: {} };
}
function primaryChainsOutsideAppliesTo(draft) {
  const applies = draft.chains ?? [];
  if (applies.length === 0) return [];
  const inScope = new Set(applies);
  return (draft.primaryChains ?? []).filter((c) => !inScope.has(c));
}
function isChainAgnostic(draft) {
  return !draft.chains || draft.chains.length === 0;
}

// src/catalog/ids.ts
var CATALOG_ID_RE = /^(?:default_)?(?:address_(?=label_))?(tag|label|entity|group)_[0-9a-f]{64}$/;
function catalogKindOfId(value) {
  if (typeof value !== "string") return null;
  const match = CATALOG_ID_RE.exec(value);
  return match ? match[1] : null;
}
function catalogIdsByKind(values) {
  const out = {};
  for (const value of values) {
    const kind = catalogKindOfId(value);
    if (kind) (out[kind] ??= /* @__PURE__ */ new Set()).add(value);
  }
  return Object.fromEntries(Object.entries(out).map(([kind, ids]) => [kind, [...ids].sort()]));
}

// src/chains/family.ts
var ChainFamily = {
  EVM: "evm",
  UTXO: "utxo",
  SOLANA: "solana",
  /** Hyperliquid's L1 (HyperCore): one signed action per transaction, EVM-shaped identifiers. */
  HYPERCORE: "hypercore",
  OTHER: "other"
};

// src/chains/seed-data.ts
var CHAIN_MAPPING = {
  // --- EVM mainnets ---
  "0x1": "eth",
  "0x3d": "etc",
  "0x89": "polygon",
  "0x38": "bsc",
  "0xcc": "opbnb",
  "0xa86a": "avalanche",
  "0xfa": "fantom",
  "0x19": "cronos",
  "0x2a15c308d": "palm",
  "0xa4b1": "arbitrum",
  "0x64": "gnosis",
  "0x15b38": "chiliz",
  "0x2105": "base",
  "0xa": "optimism",
  "0x2019": "klaytn",
  "0xa4ec": "celo",
  "0x8ae": "kava",
  "0x440": "metis",
  "0x63564c40": "harmony",
  "0x171": "pulsechain",
  "0x1388": "mantle",
  // Nesa. A Cosmos-SDK chain with an EVM module, so its Cosmos chain id
  // (`nesa_41443-1`) and its EVM chain id are the same 41443. Ported from
  // `_seed_data.py`, which has carried it since the Hyperlane domain fallback
  // was retired — without this row every spelling of Nesa falls through the
  // registry unresolved and renders as a raw `eip155:41443`.
  "0xa1e3": "nesa",
  "0x505": "moonriver",
  "0x504": "moonbeam",
  "0x144": "zkSync_era",
  "0x44d": "polygon_zkevm",
  "0xe708": "linea",
  "0x82750": "scroll",
  "0x13e31": "blast",
  "0x4e454152": "aurora",
  "0x7a": "fuse",
  "0x2eb": "flow",
  "0x7e4": "ronin",
  "0x46f": "lisk",
  "0x1b58": "zetachain",
  "0x531": "sei",
  // HyperEVM. Its L1 order book (HyperCore) is a non-EVM chain in registry.ts.
  "0x3e7": "hyperevm",
  // Ink, Kraken's OP Stack L2 — ported from `_seed_data.py` (CCIP + LayerZero route to it).
  "0xdef1": "ink",
  "0x8f": "monad",
  "0x2611": "plasma",
  // peaq. Ported from `_seed_data.py`, where it was seeded because a LayerZero
  // OFT leg named its endpoint id and the chip rendered the bare number. The
  // lower-case name is the project's own styling.
  "0xd0a": "peaq",
  // GRVT, a ZK Stack chain. This id was seeded as `zkSync_era_testnet`, so
  // `eip155:325` resolved as a zkSync Era testnet paired with Era mainnet; Era's
  // testnets are 280 and 300. Ported from `_seed_data.py`, which carries the
  // sources for the id and for the native token (GBT, not ETH).
  "0x145": "grvt",
  // --- EVM testnets ---
  "0x5": "goerli",
  "0xaa36a7": "sepolia",
  "0x4268": "holesky",
  "0x13881": "mumbai",
  "0x13882": "polygon_amoy",
  "0x61": "bsc_testnet",
  "0x27d8": "gnosis_chiado",
  "0x15b32": "chiliz_spicy",
  "0x14a33": "base_goerli",
  "0x14a34": "base_sepolia",
  "0x1a4": "optimism_goerli",
  "0xaa37dc": "optimism_sepolia",
  "0x66eee": "arbitrum_sepolia",
  "0x3e9": "klaytn_baobab",
  "0xaef3": "celo_alfajores",
  "0x8ad": "kava_testnet",
  "0x24c": "metis_goerli",
  "0x6357d2e0": "harmony_testnet",
  "0x4e454153": "aurora_testnet",
  "0x507": "moonbase",
  "0x118": "zkSync_testnet",
  "0x12c": "zksync_sepolia",
  "0x7b": "fuse_testnet",
  "0xe704": "linea_testnet",
  "0xe705": "linea_sepolia",
  "0x8274f": "scroll_testnet",
  "0x138b": "mantle_sepolia",
  "0xa0c71fd": "blast_sepolia",
  "0x98a": "polygon_zkevm_cardona",
  "0xfa2": "fantom_testnet",
  "0x106a": "lisk_sepolia",
  "0x221": "flow_testnet",
  "0x7e5": "ronin_saigon",
  "0x1b59": "zetachain_testnet",
  "0x530": "sei_testnet",
  "0x3e6": "hyperevm_testnet"
};
var TESTNET_CHAINS = [
  "goerli",
  "sepolia",
  "mumbai",
  "bsc_testnet",
  "gnosis_chiado",
  "chiliz_spicy",
  "base_goerli",
  "optimism_goerli",
  "klaytn_baobab",
  "celo_alfajores",
  "kava_testnet",
  "metis_goerli",
  "harmony_testnet",
  "zkSync_testnet",
  "aurora_testnet",
  "moonbase",
  "fuse_testnet",
  "linea_testnet",
  "scroll_testnet",
  "holesky",
  "polygon_amoy",
  "base_sepolia",
  "optimism_sepolia",
  "arbitrum_sepolia",
  "linea_sepolia",
  "blast_sepolia",
  "mantle_sepolia",
  "zksync_sepolia",
  "polygon_zkevm_cardona",
  "fantom_testnet",
  "lisk_sepolia",
  "flow_testnet",
  "ronin_saigon",
  "zetachain_testnet",
  "sei_testnet",
  "hyperevm_testnet"
];
var LAYER1_CHAINS = [
  "eth",
  "etc",
  "bsc",
  "avalanche",
  "fantom",
  "klaytn",
  "celo",
  "kava",
  "harmony",
  "moonriver",
  "moonbeam",
  "fuse",
  "pulsechain",
  "flow",
  "ronin",
  "sei",
  "monad",
  "peaq",
  "zetachain",
  "hyperevm"
];
var LAYER2_CHAINS = [
  "polygon",
  "arbitrum",
  "optimism",
  "metis",
  "base",
  "zkSync_era",
  "linea",
  "scroll",
  "mantle",
  "opbnb",
  "polygon_zkevm",
  "blast",
  "lisk",
  "grvt",
  "ink"
];
var SIDECHAINS = ["palm", "gnosis", "cronos", "aurora"];
var CHAIN_CONFIG = {
  "0x1": { token: "ETH", gasUnit: "Gwei", name: "Ethereum" },
  "0x3d": { token: "ETC", gasUnit: "Gwei", name: "Ethereum Classic" },
  "0x89": { token: "MATIC", gasUnit: "Gwei", name: "Polygon" },
  "0x38": { token: "BNB", gasUnit: "Gwei", name: "BNB Chain" },
  // Gas on Arbitrum is ETH; ARB is a governance token.
  "0xa4b1": { token: "ETH", gasUnit: "Gwei", name: "Arbitrum" },
  "0x2105": { token: "ETH", gasUnit: "Gwei", name: "Base" },
  "0xa": { token: "ETH", gasUnit: "Gwei", name: "Optimism" },
  "0xe708": { token: "ETH", gasUnit: "Gwei", name: "Linea" },
  "0xa86a": { token: "AVAX", gasUnit: "nAVAX", name: "Avalanche" },
  "0xfa": { token: "FTM", gasUnit: "Gwei", name: "Fantom" },
  "0x19": { token: "CRO", gasUnit: "Gwei", name: "Cronos" },
  "0x64": { token: "xDAI", gasUnit: "Gwei", name: "Gnosis" },
  "0x15b38": { token: "CHZ", gasUnit: "Gwei", name: "Chiliz" },
  "0x171": { token: "PLS", gasUnit: "Gwei", name: "PulseChain" },
  "0x504": { token: "GLMR", gasUnit: "Gwei", name: "Moonbeam" },
  "0x505": { token: "MOVR", gasUnit: "Gwei", name: "Moonriver" },
  "0x13e31": { token: "ETH", gasUnit: "Gwei", name: "Blast" },
  "0x144": { token: "ETH", gasUnit: "Gwei", name: "zkSync Era" },
  "0x1388": { token: "MNT", gasUnit: "Gwei", name: "Mantle" },
  "0xa1e3": { token: "NES", gasUnit: "Gwei", name: "Nesa" },
  "0xd0a": { token: "PEAQ", gasUnit: "Gwei", name: "peaq" },
  // GRVT pays gas in GBT (its ZKsync Bridgehub base token), not ETH.
  "0x145": { token: "GBT", gasUnit: "Gwei", name: "GRVT" },
  "0xcc": { token: "BNB", gasUnit: "Gwei", name: "opBNB" },
  // Gas on Polygon zkEVM is ETH, not MATIC.
  "0x44d": { token: "ETH", gasUnit: "Gwei", name: "Polygon zkEVM" },
  "0x1b58": { token: "ZETA", gasUnit: "Gwei", name: "ZetaChain" },
  "0x82750": { token: "ETH", gasUnit: "Gwei", name: "Scroll" },
  "0x4e454152": { token: "ETH", gasUnit: "Gwei", name: "Aurora" },
  "0x531": { token: "SEI", gasUnit: "Gwei", name: "Sei" },
  "0x3e7": { token: "HYPE", gasUnit: "Gwei", name: "HyperEVM" },
  "0xdef1": { token: "ETH", gasUnit: "Gwei", name: "Ink" },
  "0x3e6": { token: "HYPE", gasUnit: "Gwei", name: "HyperEVM Testnet" },
  "0x8f": { token: "MON", gasUnit: "Gwei", name: "Monad" },
  "0x3e9": { token: "KLAY", gasUnit: "Gwei", name: "Klaytn Baobab" },
  "0x4e454153": { token: "ETH", gasUnit: "Gwei", name: "Aurora Testnet" },
  "0x8274f": { token: "ETH", gasUnit: "Gwei", name: "Scroll Sepolia" },
  "0x530": { token: "SEI", gasUnit: "Gwei", name: "Sei Testnet" },
  "0x2eb": { token: "FLOW", gasUnit: "FLOW units", name: "Flow" },
  "0x7e4": { token: "RON", gasUnit: "Gwei", name: "Ronin" },
  "0x46f": { token: "LSK", gasUnit: "LSK units", name: "Lisk" },
  "0xaa36a7": { token: "ETH", gasUnit: "Gwei", name: "Sepolia" },
  "0x4268": { token: "ETH", gasUnit: "Gwei", name: "Holesky" },
  "0x13882": { token: "MATIC", gasUnit: "Gwei", name: "Polygon Amoy" },
  "0x61": { token: "tBNB", gasUnit: "Gwei", name: "BSC Testnet" },
  "0x66eee": { token: "ETH", gasUnit: "Gwei", name: "Arbitrum Sepolia" },
  "0x14a34": { token: "ETH", gasUnit: "Gwei", name: "Base Sepolia" },
  "0xaa37dc": { token: "ETH", gasUnit: "Gwei", name: "Optimism Sepolia" },
  "0xe705": { token: "ETH", gasUnit: "Gwei", name: "Linea Sepolia" },
  "0xfa2": { token: "FTM", gasUnit: "Gwei", name: "Fantom Testnet" },
  "0x27d8": { token: "xDAI", gasUnit: "Gwei", name: "Gnosis Chiado" },
  "0x15b32": { token: "CHZ", gasUnit: "Gwei", name: "Chiliz Testnet" },
  "0x507": { token: "DEV", gasUnit: "Gwei", name: "Moonbase" },
  "0xa0c71fd": { token: "ETH", gasUnit: "Gwei", name: "Blast Sepolia" },
  "0x12c": { token: "ETH", gasUnit: "Gwei", name: "zkSync Sepolia" },
  "0x138b": { token: "MNT", gasUnit: "Gwei", name: "Mantle Sepolia" },
  "0x98a": { token: "MATIC", gasUnit: "Gwei", name: "Polygon zkEVM Cardona" },
  "0x1b59": { token: "ZETA", gasUnit: "Gwei", name: "Zetachain Testnet" },
  "0x221": { token: "FLOW", gasUnit: "FLOW units", name: "Flow Testnet" },
  "0x7e5": { token: "RON", gasUnit: "Gwei", name: "Ronin Saigon Testnet" },
  "0x106a": { token: "LSK", gasUnit: "LSK units", name: "Lisk Sepolia" },
  "0x0": { token: "BTC", gasUnit: "Sats/byte", name: "Bitcoin" },
  "0x1399c": { token: "SOL", gasUnit: "Lamports", name: "Solana" },
  "0x162": { token: "DOT", gasUnit: "Plancks", name: "Polkadot" },
  "0x2611": { token: "XPL", gasUnit: "Gwei", name: "Plasma" },
  // Without a row, `seedFromCanonical` names a chain by its slug ("celo") and
  // gives it ETH. Every seeded chain has one; the rows below were added in
  // `_seed_data.py` first and ported, and the parity test keeps them in step.
  "0xa4ec": { token: "CELO", gasUnit: "Gwei", name: "Celo" },
  "0x440": { token: "METIS", gasUnit: "Gwei", name: "Metis" },
  "0x8ae": { token: "KAVA", gasUnit: "Gwei", name: "Kava" },
  "0x63564c40": { token: "ONE", gasUnit: "Gwei", name: "Harmony" },
  "0x7a": { token: "FUSE", gasUnit: "Gwei", name: "Fuse" },
  "0x2019": { token: "KLAY", gasUnit: "Gwei", name: "Klaytn" },
  "0x2a15c308d": { token: "PALM", gasUnit: "Gwei", name: "Palm" },
  "0x5": { token: "ETH", gasUnit: "Gwei", name: "Goerli" },
  "0x13881": { token: "MATIC", gasUnit: "Gwei", name: "Polygon Mumbai" },
  "0x14a33": { token: "ETH", gasUnit: "Gwei", name: "Base Goerli" },
  "0x1a4": { token: "ETH", gasUnit: "Gwei", name: "Optimism Goerli" },
  "0xaef3": { token: "CELO", gasUnit: "Gwei", name: "Celo Alfajores" },
  "0x8ad": { token: "TKAVA", gasUnit: "Gwei", name: "Kava Testnet" },
  "0x24c": { token: "METIS", gasUnit: "Gwei", name: "Metis Goerli" },
  "0x6357d2e0": { token: "ONE", gasUnit: "Gwei", name: "Harmony Testnet" },
  "0x118": { token: "ETH", gasUnit: "Gwei", name: "zkSync Goerli" },
  "0x7b": { token: "SPARK", gasUnit: "Gwei", name: "Fuse Sparknet" },
  "0xe704": { token: "ETH", gasUnit: "Gwei", name: "Linea Goerli" }
};
var EXPLORER_URLS = {
  eth: "https://etherscan.io",
  etc: "https://blockscout.com/etc/mainnet",
  polygon: "https://polygonscan.com",
  bsc: "https://bscscan.com",
  avalanche: "https://snowtrace.io",
  fantom: "https://ftmscan.com",
  arbitrum: "https://arbiscan.io",
  optimism: "https://optimistic.etherscan.io",
  zksync_era: "https://explorer.zksync.io",
  base: "https://basescan.org",
  linea: "https://lineascan.build",
  scroll: "https://scrollscan.com",
  mantle: "https://explorer.mantle.xyz",
  // The EVM-side Blockscout. Nesa also runs a Cosmos explorer at
  // `explorer.nesa.ai`, which cannot resolve an `0x` address or tx hash.
  nesa: "https://explorer-evm.nesa.ai",
  // Subscan, whose EVM mode answers the default /tx, /address, /block and
  // /token paths (each checked against a live peaq tx on 2026-09-16).
  peaq: "https://peaq.subscan.io",
  opbnb: "https://opbnbscan.com",
  polygon_zkevm: "https://zkevm.polygonscan.com",
  blast: "https://blastscan.io",
  pulsechain: "https://scan.pulsechain.com",
  aurora: "https://explorer.aurora.dev",
  flow: "https://evm.flowscan.io",
  ronin: "https://app.roninchain.com",
  lisk: "https://blockscout.lisk.com",
  zetachain: "https://explorer.zetachain.com",
  sei: "https://seitrace.com",
  hyperevm: "https://hyperevmscan.io",
  ink: "https://explorer.inkonchain.com",
  monad: "https://monadexplorer.com",
  plasma: "https://plasmascan.to",
  // --- Testnets ---
  // A testnet with no row here gets NO explorer link — the registry does not
  // lend it its mainnet's, which would show a different chain's object under
  // the same hash or address. So a row belongs here only when the testnet has
  // its own explorer that is live; these were checked on 2026-09-16 (DNS, the
  // site answering, and the chain id where the explorer exposes one).
  //
  // Deliberately absent, because the explorer is gone or now serves another
  // network: holesky (holesky.etherscan.io no longer resolves), mumbai
  // (mumbai.polygonscan.com no longer resolves — the Python table still carries
  // it), base_goerli, optimism_goerli, linea_testnet, zkSync_testnet,
  // metis_goerli, polygon_zkevm_cardona, fantom_testnet, celo_alfajores (both
  // its explorers redirect to Celo Sepolia), scroll_testnet
  // (sepolia.scrollscan.com is a Cloudflare 1014), lisk_sepolia (unreachable)
  // and sei_testnet (Seitrace picks the network by query string, which the link
  // builder cannot express).
  //
  // FE-only: the Python table has no mainnet fallback to correct, and feeds
  // scraping hosts, so these are not mirrored there.
  goerli: "https://goerli.etherscan.io",
  sepolia: "https://sepolia.etherscan.io",
  polygon_amoy: "https://amoy.polygonscan.com",
  bsc_testnet: "https://testnet.bscscan.com",
  gnosis_chiado: "https://gnosis-chiado.blockscout.com",
  base_sepolia: "https://sepolia.basescan.org",
  optimism_sepolia: "https://sepolia-optimism.etherscan.io",
  arbitrum_sepolia: "https://sepolia.arbiscan.io",
  aurora_testnet: "https://explorer.testnet.aurora.dev",
  moonbase: "https://moonbase.moonscan.io",
  fuse_testnet: "https://explorer.fusespark.io",
  linea_sepolia: "https://sepolia.lineascan.build",
  blast_sepolia: "https://sepolia.blastscan.io",
  mantle_sepolia: "https://sepolia.mantlescan.xyz",
  zksync_sepolia: "https://sepolia.explorer.zksync.io",
  flow_testnet: "https://testnet.evm.flow.com",
  ronin_saigon: "https://saigon-app.roninchain.com",
  zetachain_testnet: "https://testnet.zetascan.com"
};
var TESTNET_TO_MAINNET = {
  goerli: "eth",
  sepolia: "eth",
  mumbai: "polygon",
  bsc_testnet: "bsc",
  gnosis_chiado: "gnosis",
  chiliz_spicy: "chiliz",
  base_goerli: "base",
  optimism_goerli: "optimism",
  klaytn_baobab: "klaytn",
  celo_alfajores: "celo",
  kava_testnet: "kava",
  metis_goerli: "metis",
  harmony_testnet: "harmony",
  zkSync_testnet: "zkSync_era",
  aurora_testnet: "aurora",
  moonbase: "moonbeam",
  fuse_testnet: "fuse",
  linea_testnet: "linea",
  scroll_testnet: "scroll",
  holesky: "eth",
  polygon_amoy: "polygon",
  base_sepolia: "base",
  optimism_sepolia: "optimism",
  arbitrum_sepolia: "arbitrum",
  linea_sepolia: "linea",
  blast_sepolia: "blast",
  mantle_sepolia: "mantle",
  zksync_sepolia: "zkSync_era",
  polygon_zkevm_cardona: "polygon_zkevm",
  fantom_testnet: "fantom",
  lisk_sepolia: "lisk",
  flow_testnet: "flow",
  ronin_saigon: "ronin",
  zetachain_testnet: "zetachain",
  sei_testnet: "sei",
  hyperevm_testnet: "hyperevm"
};
var CHAIN_ALIASES = {
  "eth": ["ethereum", "ethereum-mainnet", "eth-mainnet"],
  "polygon": ["matic", "matic-network", "polygon-mainnet", "polygon-pos"],
  "bsc": ["binance", "binance-smart-chain", "bnb", "bnb-chain", "bnb-smart-chain"],
  "avalanche": ["avax", "avalanche-c-chain", "avax-c-chain", "avalanche-mainnet"],
  "arbitrum": ["arbitrum-one", "arb", "arbitrum-mainnet"],
  "optimism": ["op", "op-mainnet", "optimism-mainnet"],
  "base": ["base-mainnet"],
  "gnosis": ["xdai", "gnosis-chain"],
  "fantom": ["ftm", "fantom-opera"],
  "cronos": ["cro"],
  "linea": ["linea-mainnet"],
  "scroll": ["scroll-mainnet"],
  "blast": ["blast-mainnet"],
  "ronin": ["ron"],
  "sei": ["sei-mainnet"],
  "pulsechain": ["pls"],
  "polygon_zkevm": ["polygon-zkevm", "zkevm"],
  "zksync_era": ["zksync", "zksync-era", "era"],
  "mantle_sepolia": ["mantle-testnet", "mantle_testnet"],
  // Non-EVM — the short tickers callers reach for.
  "solana": ["sol"],
  "bitcoin": ["btc"],
  "polkadot": ["dot"],
  "hypercore": ["hyperliquid", "hyperliquid-core", "hyperliquid-l1"],
  "hyperevm": ["hyper-evm", "hyperliquid-evm"],
  "hypercore-testnet": ["hyperliquid-testnet", "hypercore_testnet"],
  "hyperevm_testnet": ["hyperevm-testnet", "hyperliquid-evm-testnet"],
  "monero": ["xmr"],
  "zcash": ["zec"]
};

// src/singleton/index.ts
var NAMESPACE = "@authe/fe-commons/singleton/";
function globalSingleton(key, init) {
  const slot = /* @__PURE__ */ Symbol.for(NAMESPACE + key);
  const realm = globalThis;
  const existing = realm[slot];
  if (existing !== void 0) return existing;
  const created = init();
  realm[slot] = created;
  return created;
}

// src/chains/registry.ts
function foldAliases(...groups) {
  const seen = /* @__PURE__ */ new Set();
  for (const group of groups) {
    for (const raw of group ?? []) {
      const alias = raw.trim().toLowerCase();
      if (alias) seen.add(alias);
    }
  }
  return [...seen];
}
var ChainRegistry = class {
  bySlug = /* @__PURE__ */ new Map();
  byHex = /* @__PURE__ */ new Map();
  byCaip2 = /* @__PURE__ */ new Map();
  byAlias = /* @__PURE__ */ new Map();
  register(chain, { replace = false } = {}) {
    const existingSlug = this.bySlug.get(chain.slug);
    if (existingSlug && existingSlug !== chain && !replace) {
      throw new Error(
        `Chain slug "${chain.slug}" already registered; pass replace=true to overwrite.`
      );
    }
    if (chain.chainIdHex) {
      const existingHex = this.byHex.get(chain.chainIdHex);
      if (existingHex && existingHex.slug !== chain.slug && !replace) {
        throw new Error(
          `Chain hex ${chain.chainIdHex} already mapped to slug ${existingHex.slug}; pass replace=true to overwrite.`
        );
      }
    }
    if (existingSlug) this.dropAliases(existingSlug);
    this.bySlug.set(chain.slug, chain);
    if (chain.chainIdHex) this.byHex.set(chain.chainIdHex, chain);
    if (chain.caip2) this.byCaip2.set(chain.caip2, chain);
    for (const alias of foldAliases(chain.aliases)) this.byAlias.set(alias, chain);
  }
  dropAliases(chain) {
    for (const alias of foldAliases(chain.aliases)) {
      if (this.byAlias.get(alias) === chain) this.byAlias.delete(alias);
    }
  }
  unregister(slug) {
    const chain = this.bySlug.get(slug);
    if (!chain) return null;
    this.bySlug.delete(slug);
    if (chain.chainIdHex) this.byHex.delete(chain.chainIdHex);
    if (chain.caip2) this.byCaip2.delete(chain.caip2);
    this.dropAliases(chain);
    return chain;
  }
  clear() {
    this.bySlug.clear();
    this.byHex.clear();
    this.byCaip2.clear();
    this.byAlias.clear();
  }
  get(slug) {
    return this.bySlug.get(slug) ?? null;
  }
  /** Lookup by EVM hex chain id (auto `0x`-prefixes + lowercases). */
  getByHex(chainIdHex) {
    let normalized = chainIdHex.toLowerCase();
    if (!normalized.startsWith("0x")) normalized = "0x" + normalized.replace(/^0x/, "");
    return this.byHex.get(normalized) ?? null;
  }
  /** Lookup by CAIP-2 chain id (e.g. "eip155:1"). */
  getByCaip2(caip2) {
    return this.byCaip2.get(normalizeCaip2(caip2)) ?? null;
  }
  /**
   * Resolve by CAIP-2, hex, bare decimal, slug, or alias — in that order, the
   * same order as `ChainRegistry.resolve` in `commons/chains/registry.py`, so a
   * spelling resolves to the same chain on both sides of the wire.
   */
  resolve(value) {
    if (!value) return null;
    if (value.includes(":")) {
      const hit = this.getByCaip2(value);
      if (hit) return hit;
    }
    if (value.startsWith("0x")) {
      const hit = this.getByHex(value);
      if (hit) return hit;
    }
    if (/^\d+$/.test(value)) {
      const hit = this.getByHex("0x" + BigInt(value).toString(16));
      if (hit) return hit;
    }
    const folded = value.toLowerCase();
    return this.get(folded) ?? this.byAlias.get(folded) ?? null;
  }
  byFamily(family) {
    return [...this.bySlug.values()].filter((c) => c.family === family).sort((a, b) => a.slug.localeCompare(b.slug));
  }
  all() {
    return [...this.bySlug.values()].sort((a, b) => a.slug.localeCompare(b.slug));
  }
};
function normalizeCaip2(value) {
  const split = value.trim().indexOf(":");
  if (split < 0) return value.trim();
  const namespace = value.trim().slice(0, split).toLowerCase();
  let reference = value.trim().slice(split + 1);
  if (["bip122", "polkadot"].includes(namespace)) reference = reference.toLowerCase();
  if (namespace === "eip155" && /^\d+$/.test(reference)) reference = BigInt(reference).toString();
  return `${namespace}:${reference}`;
}
function toCanonicalChainId(value) {
  if (!value) return null;
  const chain = registry.resolve(value);
  if (!chain) return (value.includes(":") ? normalizeCaip2(value) : value.trim().toLowerCase()) || null;
  return chain.caip2 ?? chain.chainIdHex ?? chain.slug;
}
var toStoredChainId = toCanonicalChainId;
function toChainSlug(value) {
  if (!value) return null;
  return registry.resolve(value)?.slug ?? value;
}
globalSingleton("chains/explorers", () => ({
  overrides: /* @__PURE__ */ new Map(),
  pathDefaults: {}
}));
var REORG_DEPTHS_BY_SLUG = {
  eth: 12,
  polygon: 256,
  matic: 256,
  bsc: 15,
  arbitrum: 12,
  optimism: 12,
  base: 12,
  avalanche: 12,
  fantom: 12,
  cronos: 12,
  gnosis: 12,
  linea: 12,
  scroll: 12,
  mantle: 12,
  zksync: 12,
  zksync_era: 12,
  metis: 12,
  celo: 12,
  klaytn: 12,
  moonbeam: 12,
  moonriver: 12,
  harmony: 12,
  aurora: 12,
  fuse: 12,
  palm: 12,
  chiliz: 12,
  kava: 12,
  blast: 12,
  bitcoin: 6,
  solana: 32,
  // Monero's ten-block spend lock is a network rule; Zcash needs many more
  // blocks than Bitcoin for equal work at a 75-second block.
  monero: 10,
  zcash: 24,
  // HyperBFT commits a block final.
  hypercore: 1,
  hyperevm: 1,
  hyperevm_testnet: 1
};
var BLOCK_TIME_BY_SLUG = {
  eth: 12,
  etc: 13,
  polygon: 2,
  matic: 2,
  bsc: 3,
  arbitrum: 0.25,
  optimism: 2,
  base: 2,
  avalanche: 2,
  fantom: 1,
  cronos: 6,
  gnosis: 5,
  linea: 3,
  scroll: 3,
  mantle: 2,
  zksync: 1,
  zksync_era: 1,
  metis: 4,
  celo: 5,
  klaytn: 1,
  moonbeam: 12,
  moonriver: 12,
  harmony: 2,
  aurora: 1,
  fuse: 5,
  blast: 2,
  bitcoin: 600,
  solana: 0.4,
  polkadot: 6,
  monero: 120,
  zcash: 75,
  hypercore: 0.2,
  "hypercore-testnet": 0.2,
  hyperevm: 1
};
var EXPLORER_SUPPLEMENT = {
  bitcoin: "https://blockstream.info",
  solana: "https://solscan.io",
  polkadot: "https://polkadot.subscan.io",
  monero: "https://xmrchain.net",
  // Blockchair's base carries the coin as a path segment, which is exactly
  // what the link builder appends to — `/zcash` + `/transaction/<hash>`.
  zcash: "https://blockchair.com/zcash",
  // app.hyperliquid.xyz/explorer/tx/<hash>, /address/<addr>, /block/<n>.
  hypercore: "https://app.hyperliquid.xyz/explorer",
  "hypercore-testnet": "https://app.hyperliquid-testnet.xyz/explorer",
  cronos: "https://cronoscan.com",
  gnosis: "https://gnosisscan.io",
  celo: "https://celoscan.io",
  moonbeam: "https://moonscan.io",
  zkSync_era: "https://explorer.zksync.io",
  matic: "https://polygonscan.com"
};
function explorerFor(slug, rawSlug) {
  return EXPLORER_URLS[slug] ?? EXPLORER_URLS[rawSlug] ?? EXPLORER_SUPPLEMENT[slug] ?? EXPLORER_SUPPLEMENT[rawSlug] ?? null;
}
var BRAND_COLOR_BY_SLUG = {
  eth: "#627EEA",
  etc: "#328332",
  polygon: "#8247E5",
  matic: "#8247E5",
  bsc: "#F0B90B",
  arbitrum: "#28A0F0",
  optimism: "#FF0420",
  base: "#0052FF",
  avalanche: "#E84142",
  fantom: "#1969FF",
  cronos: "#002D74",
  gnosis: "#0B6E4F",
  linea: "#61DFFF",
  scroll: "#EBC28E",
  mantle: "#000000",
  zksync: "#8C8DFC",
  zksync_era: "#8C8DFC",
  metis: "#00DACC",
  celo: "#FCFF52",
  klaytn: "#FF1B1B",
  moonbeam: "#53CBC8",
  moonriver: "#C0C0C0",
  harmony: "#00AEE9",
  aurora: "#70D44B",
  fuse: "#B4F9BA",
  blast: "#FCFC03",
  kava: "#FF433E",
  bitcoin: "#F7931A",
  solana: "#14F195",
  polkadot: "#E6007A",
  monero: "#FF6600",
  zcash: "#F4B728",
  hypercore: "#97FCE4",
  hyperevm: "#97FCE4"
};
function brandColorFor(slug, mainnetSlug) {
  return BRAND_COLOR_BY_SLUG[slug] ?? (mainnetSlug ? BRAND_COLOR_BY_SLUG[mainnetSlug] ?? null : null);
}
var LOGO_URL_BY_SLUG = {};
var CAIP2_BY_SLUG = {
  "bitcoin-testnet": "bip122:000000000933ea01ad0ee984209779ba",
  "solana-devnet": "solana:EtWTRABZaYq6iMfeYKouRu166VU2xqa1",
  "solana-testnet": "solana:4uhcVJyU9pJkvQyS88uRDiswHXSCkY3z",
  bitcoin: "bip122:000000000019d6689c085ae165831e93",
  solana: "solana:5eykt4UsFv8P8NJdTREpY1vzqKqZKvdp",
  polkadot: "polkadot:91b171bb158e2d3848fa23a9f1c25182",
  // Zcash is a Bitcoin fork, so `bip122` is its registered namespace and the
  // reference is its genesis hash truncated to 32 chars — Bitcoin's own rule.
  zcash: "bip122:00040fe8ec8471911baa1db1266ea15d",
  // Monero has no CASA-registered namespace; `monero` is a platform-local
  // choice. See the note on the Python side, which this table must match
  // byte for byte — `test_chains_fe_registry_parity.py` fails if it drifts.
  monero: "monero:418015bb9ae982a1975da7d79277c270",
  // HyperCore has no CASA namespace, and `hyperliquid` / `hypercore` exceed
  // CAIP-2's eight-character namespace limit. Platform-local; matches Python.
  hypercore: "hyperliq:mainnet",
  "hypercore-testnet": "hyperliq:testnet"
};
function deriveCaip2(slug, family, chainIdHex) {
  if (CAIP2_BY_SLUG[slug]) return CAIP2_BY_SLUG[slug];
  if (family === ChainFamily.EVM && chainIdHex) {
    return `eip155:${BigInt(chainIdHex).toString(10)}`;
  }
  return null;
}
var NON_EVM_OVERRIDES = {
  "bitcoin-testnet": { family: ChainFamily.UTXO, decimals: 8, chainIdHex: null, name: "Bitcoin Testnet", nativeToken: "BTC", mainnetSlug: "bitcoin" },
  "solana-devnet": { family: ChainFamily.SOLANA, decimals: 9, chainIdHex: null, name: "Solana Devnet", nativeToken: "SOL", mainnetSlug: "solana" },
  "solana-testnet": { family: ChainFamily.SOLANA, decimals: 9, chainIdHex: null, name: "Solana Testnet", nativeToken: "SOL", mainnetSlug: "solana" },
  "hypercore-testnet": { family: ChainFamily.HYPERCORE, decimals: 8, chainIdHex: null, name: "HyperCore Testnet", nativeToken: "HYPE", mainnetSlug: "hypercore" },
  bitcoin: { family: ChainFamily.UTXO, decimals: 8, chainIdHex: null, name: "Bitcoin", nativeToken: "BTC", aliases: ["bitcoin-mainnet", "0x0"] },
  solana: { family: ChainFamily.SOLANA, decimals: 9, chainIdHex: null, name: "Solana", nativeToken: "SOL", aliases: ["solana-mainnet", "0x1399c"] },
  polkadot: { family: ChainFamily.OTHER, decimals: 10, chainIdHex: null, name: "Polkadot", nativeToken: "DOT", aliases: ["polkadot-mainnet", "0x162"] },
  monero: { family: ChainFamily.UTXO, decimals: 12, chainIdHex: null, name: "Monero", nativeToken: "XMR", aliases: ["monero-mainnet"] },
  zcash: { family: ChainFamily.UTXO, decimals: 8, chainIdHex: null, name: "Zcash", nativeToken: "ZEC", aliases: ["zcash-mainnet"] },
  hypercore: { family: ChainFamily.HYPERCORE, decimals: 8, chainIdHex: null, name: "HyperCore", nativeToken: "HYPE", aliases: ["hypercore-mainnet"] }
};
function layerFor(slug) {
  const folded = slug.toLowerCase();
  const listedIn = (listed) => listed.some((member) => member.toLowerCase() === folded);
  if (listedIn(LAYER1_CHAINS)) return "l1";
  if (listedIn(LAYER2_CHAINS)) return "l2";
  if (listedIn(SIDECHAINS)) return "sidechain";
  return "unknown";
}
function seedFromCanonical(target) {
  for (const [hexId, rawSlug] of Object.entries(CHAIN_MAPPING)) {
    const slug = rawSlug.toLowerCase();
    const config = CHAIN_CONFIG[hexId];
    const token = config?.token ?? "ETH";
    const override = NON_EVM_OVERRIDES[slug];
    const family = override?.family ?? ChainFamily.EVM;
    const decimals = override?.decimals ?? 18;
    const isTestnet = TESTNET_CHAINS.includes(rawSlug);
    let mainnetSlug = null;
    if (isTestnet) {
      const m = TESTNET_TO_MAINNET[rawSlug];
      if (m) mainnetSlug = m.toLowerCase();
    }
    const reorgDepth = REORG_DEPTHS_BY_SLUG[slug] ?? 12;
    const chainIdHex = hexId.toLowerCase();
    const metadata = { layer: layerFor(slug) };
    if (config?.gasUnit) metadata.gasUnit = config.gasUnit;
    if (slug === "etc") {
      metadata.fork_of = "eth";
      metadata.note = "ETC shares pre-fork tx hashes with ETH. Audit sec. 2 calls out this collision risk.";
    }
    try {
      target.register(
        {
          slug,
          chainIdHex,
          family,
          name: config?.name ?? rawSlug,
          nativeToken: token,
          decimals,
          isTestnet,
          mainnetSlug,
          reorgDepth,
          explorerUrl: explorerFor(slug, rawSlug),
          blockTimeSec: BLOCK_TIME_BY_SLUG[slug] ?? (mainnetSlug ? BLOCK_TIME_BY_SLUG[mainnetSlug] ?? null : null),
          caip2: deriveCaip2(slug, family, chainIdHex),
          brandColor: brandColorFor(slug, mainnetSlug),
          logoUrl: LOGO_URL_BY_SLUG[slug] ?? (mainnetSlug ? LOGO_URL_BY_SLUG[mainnetSlug] ?? null : null),
          metadata,
          aliases: foldAliases(override?.aliases, CHAIN_ALIASES[slug])
        },
        { replace: true }
      );
    } catch {
      continue;
    }
  }
  for (const [slug, ovr] of Object.entries(NON_EVM_OVERRIDES)) {
    if (target.get(slug)) continue;
    try {
      target.register(
        {
          slug,
          chainIdHex: ovr.chainIdHex,
          family: ovr.family,
          name: ovr.name,
          nativeToken: ovr.nativeToken,
          decimals: ovr.decimals,
          isTestnet: !!ovr.mainnetSlug,
          mainnetSlug: ovr.mainnetSlug ?? null,
          reorgDepth: REORG_DEPTHS_BY_SLUG[slug] ?? REORG_DEPTHS_BY_SLUG[ovr.mainnetSlug ?? ""] ?? 12,
          explorerUrl: explorerFor(slug, slug),
          blockTimeSec: BLOCK_TIME_BY_SLUG[slug] ?? null,
          caip2: deriveCaip2(slug, ovr.family, ovr.chainIdHex),
          brandColor: brandColorFor(slug, null),
          logoUrl: LOGO_URL_BY_SLUG[slug] ?? null,
          metadata: {},
          aliases: foldAliases(ovr.aliases, CHAIN_ALIASES[slug])
        },
        { replace: true }
      );
    } catch {
      continue;
    }
  }
}
var registry = globalSingleton("chains/registry", () => {
  const seeded = new ChainRegistry();
  seedFromCanonical(seeded);
  return seeded;
});

// src/display/coerce.ts
function asString(value) {
  return typeof value === "string" && value !== "" ? value : void 0;
}

// src/catalog/mappers.ts
var undef = (v) => v == null || v === "" ? void 0 : v;
var storeChain = (v) => {
  const t = undef(v);
  return t ? toStoredChainId(t) ?? t : void 0;
};
function draftToRowBody(draft) {
  const common = {
    name: draft.name.trim(),
    description: undef(draft.description),
    // Lifecycle can inherit the platform default; an assessment cannot.
    lifecycle: draft.lifecycle,
    // Null explicitly clears an assessment on edit. Omission means no patch.
    severity: draft.kind === "address" ? draft.severity : draft.severity ?? null,
    aliases: draft.aliases && draft.aliases.length > 0 ? draft.aliases : void 0,
    // Send the WHOLE desired primary-chain list whenever it's defined — including
    // `[]` to explicitly clear it — so the backend can reconcile the single-winner
    // primary pointers (set/evict/retire) off an authoritative set. Only omit when
    // `undefined` (not loaded / not part of this edit) to avoid clobbering.
    primary_chains: draft.primaryChains !== void 0 ? draft.primaryChains.map((c) => storeChain(c) ?? c) : void 0
  };
  switch (draft.kind) {
    case "tag":
      return { ...common, color: undef(draft.color), icon: undef(draft.logo), category: undef(draft.category) };
    case "label":
      return {
        ...common,
        color: undef(draft.color),
        icon: undef(draft.logo),
        risk_level: draft.riskLevel ?? null,
        address: undef(draft.address),
        chain: storeChain(draft.chain)
      };
    case "group":
      return {
        ...common,
        color: undef(draft.color),
        icon: undef(draft.logo),
        member_tag_ids: draft.memberTagIds,
        member_label_ids: draft.memberLabelIds,
        member_entity_ids: draft.memberEntityIds
      };
    case "entity":
      return {
        ...common,
        color: undef(draft.color),
        logo_s3_uri: undef(draft.logo),
        risk_level: draft.riskLevel ?? null,
        entity_type: undef(draft.entityType),
        website: undef(draft.website),
        twitter: undef(draft.twitter)
      };
    case "address":
      return {
        ...common,
        logo_s3_uri: undef(draft.logo),
        risk_level: draft.riskLevel,
        address: draft.address.trim(),
        chain: toStoredChainId(draft.chain.trim()) ?? draft.chain.trim(),
        primary_entity_id: undef(draft.primaryEntityId)
      };
  }
}
var strArr = (v) => Array.isArray(v) && v.length > 0 ? v.filter((x) => typeof x === "string") : void 0;
function rowToDraft(kind, row) {
  const base = {
    name: asString(row.name) ?? "",
    description: asString(row.description),
    aliases: strArr(row.aliases),
    primaryChains: strArr(row.primary_chains)?.map((c) => toChainSlug(c) ?? c),
    lifecycle: asString(row.lifecycle),
    severity: asString(row.severity)
  };
  switch (kind) {
    case "tag":
      return { kind, ...base, color: asString(row.color), logo: asString(row.icon), category: asString(row.category) };
    case "label":
      return {
        kind,
        ...base,
        color: asString(row.color),
        logo: asString(row.icon),
        riskLevel: asString(row.risk_level),
        address: asString(row.address),
        chain: toChainSlug(asString(row.chain)) ?? void 0
      };
    case "group":
      return {
        kind,
        ...base,
        color: asString(row.color),
        logo: asString(row.icon),
        memberTagIds: strArr(row.member_tag_ids),
        memberLabelIds: strArr(row.member_label_ids),
        memberEntityIds: strArr(row.member_entity_ids)
      };
    case "entity":
      return {
        kind,
        ...base,
        color: asString(row.color),
        logo: asString(row.logo_s3_uri),
        riskLevel: asString(row.risk_level),
        // Cast, not narrowed: a stored value outside the enum must reach the
        // form as itself — `entityTypeOptions` shows it, `validateDraft` refuses it.
        entityType: asString(row.entity_type),
        website: asString(row.website),
        twitter: asString(row.twitter)
      };
    case "address":
      return {
        kind,
        ...base,
        logo: asString(row.logo_s3_uri),
        riskLevel: asString(row.risk_level),
        address: asString(row.address) ?? "",
        chain: toChainSlug(asString(row.chain)) ?? "",
        primaryEntityId: asString(row.primary_entity_id)
      };
  }
}

// src/catalog/links.ts
var WEB_SCHEMES = /* @__PURE__ */ new Set(["http:", "https:"]);
var TWITTER_HOSTS = /* @__PURE__ */ new Set([
  "twitter.com",
  "www.twitter.com",
  "x.com",
  "www.x.com",
  "mobile.twitter.com"
]);
function webUrl(value) {
  const raw = (value ?? "").trim();
  if (!raw) return void 0;
  const candidate = /^[a-z][a-z0-9+.-]*:/i.test(raw) ? raw : `https://${raw}`;
  let parsed;
  try {
    parsed = new URL(candidate);
  } catch {
    return void 0;
  }
  if (!WEB_SCHEMES.has(parsed.protocol) || !parsed.hostname) return void 0;
  return parsed.toString();
}
function webUrlLabel(value) {
  const url = webUrl(value);
  if (!url) return (value ?? "").trim() || void 0;
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}
function twitterLink(value) {
  const raw = (value ?? "").trim();
  if (!raw) return void 0;
  let handle = raw;
  if (raw.includes("/") || /^[a-z][a-z0-9+.-]*:/i.test(raw)) {
    const url = webUrl(raw);
    if (!url) return void 0;
    const parsed = new URL(url);
    if (!TWITTER_HOSTS.has(parsed.hostname.toLowerCase())) return void 0;
    handle = parsed.pathname.split("/").filter(Boolean)[0] ?? "";
  }
  handle = handle.replace(/^@+/, "");
  if (!/^\w{1,15}$/.test(handle)) return void 0;
  return { handle: `@${handle}`, url: `https://x.com/${handle}` };
}

// src/catalog/fields.ts
var CATALOG_COMMON_FIELDS = {
  id: "id",
  name: "name",
  description: "description",
  aliases: "aliases",
  lifecycle: "lifecycle",
  severity: "severity",
  source: "source",
  confidence: "confidence",
  /** Glass-box "why" bullets rendered from evidence (`commons.evidence.explain`). */
  explanation: "explanation",
  isHumanApproved: "is_human_approved",
  systemDefault: "system_default",
  metadata: "metadata",
  overridesByChain: "overrides_by_chain",
  customerId: "customer_id",
  orgIdTenantId: "org_id_tenant_id",
  createdAt: "created_at",
  createdBy: "created_by",
  updatedAt: "updated_at",
  updatedBy: "updated_by",
  validFrom: "valid_from",
  validTo: "valid_to"
};
var ENTITY_FIELDS = {
  ...CATALOG_COMMON_FIELDS,
  entityType: "entity_type",
  riskLevel: "risk_level",
  website: "website",
  twitter: "twitter",
  /** Entities/addresses store the logo as an S3 URI (tags/labels/groups use `icon`). */
  logo: "logo_s3_uri",
  parentEntityId: "parent_entity_id",
  relatedEntityIds: "related_entity_ids"
};
var TAG_FIELDS = {
  ...CATALOG_COMMON_FIELDS,
  color: "color",
  icon: "icon",
  category: "category"
};
var LABEL_FIELDS = {
  ...CATALOG_COMMON_FIELDS,
  color: "color",
  icon: "icon",
  riskLevel: "risk_level",
  /** Labels can be bound to a specific address on a chain. */
  address: "address",
  chain: "chain"
};
var GROUP_FIELDS = {
  ...CATALOG_COMMON_FIELDS,
  color: "color",
  icon: "icon",
  memberTagIds: "member_tag_ids",
  memberLabelIds: "member_label_ids",
  memberEntityIds: "member_entity_ids"
};
var CATALOG_FIELDS = {
  entity: Object.values(ENTITY_FIELDS),
  tag: Object.values(TAG_FIELDS),
  label: Object.values(LABEL_FIELDS),
  group: Object.values(GROUP_FIELDS)
};
function catalogFields(kind) {
  return CATALOG_FIELDS[kind] ?? [];
}

// src/catalog/applicability.ts
var ALL_CHAINS = null;
function assignmentAppliesToChain(assignment, chainId) {
  return assignment.chain_id == null || assignment.chain_id === chainId;
}
function appliesToAllChains(assignments) {
  return assignments.some((a) => a.chain_id == null);
}
function assignedChains(assignments) {
  const set = /* @__PURE__ */ new Set();
  for (const a of assignments) if (a.chain_id != null) set.add(a.chain_id);
  return [...set];
}
function chainsFromAssignments(assignments) {
  return appliesToAllChains(assignments) ? [] : assignedChains(assignments);
}
function assignmentsForChain(assignments, chainId) {
  return assignments.filter((a) => assignmentAppliesToChain(a, chainId));
}

// src/tenancy/rungs.ts
var RUNG_ORDER = ["global", "customer", "org", "tenant", "user"];
var RUNG_ALIASES = {
  system: "global",
  platform: "global"
};
function isRung(value) {
  return typeof value === "string" && RUNG_ORDER.includes(value);
}
function canonicalRung(value) {
  if (typeof value !== "string") return null;
  const lower = value.trim().toLowerCase();
  if (isRung(lower)) return lower;
  return RUNG_ALIASES[lower] ?? null;
}

// src/tenancy/constants.ts
var GLOBAL_TENANT = "main";
var TENANT_PATH_ANY = "*";
var FieldOverridesSchema = z.object({ "name": z.union([z.string(), z.null()]).describe("Override the displayed name for this chain. Example: a Safe module on Polygon may want to display ``Safe (Polygon)`` while the entity name stays ``Safe`` globally.").default(null), "description": z.union([z.string(), z.null()]).describe("Optional per-chain description override (long-form).").default(null), "color": z.union([z.string(), z.null()]).describe("Hex / CSS colour override. UIs use this to colour-code the row in chain-specific lists.").default(null), "icon": z.union([z.string(), z.null()]).describe("Per-chain icon identifier (URL or token).").default(null), "logo": z.union([z.string(), z.null()]).describe("Per-chain logo URI. The single most common override: most bridged tokens / entities want a chain-flavoured logo.").default(null), "aliases": z.union([z.array(z.string()), z.null()]).describe("Per-chain aliases (e.g. ``USDC.e`` on Avalanche vs ``USDC`` everywhere else). When set, **replaces** the base list \u2014 we do not implicitly union so the override is fully predictable.").default(null), "metadata": z.union([z.record(z.string(), z.any()), z.null()]).describe("Free-form per-chain metadata overrides. When set, the dict is *merged* over the base metadata (per-key overlay) rather than replacing the whole map \u2014 metadata is a bag, not a single value.").default(null) }).strict().describe("Per-chain override of presentation fields on a catalog row.\n\nFields default to ``None`` \u2014 only set the ones that should override\nthe base catalog row for the chain in question. Unset fields fall\nthrough to the base row's values via :func:`apply_overrides`.\n\nThe fields covered here are deliberately the *presentation* surface\nevery catalog row family (Tag / Label / Entity / Group) shares.\nFamily-specific overrides (e.g. ``Entity.entity_type``) are not\noverridable on purpose \u2014 overriding identity-shaping fields would\nquietly partition entities by chain which is the exact failure mode\nwe are designing against.");

// src/catalog/overrides.ts
var hexColor2 = z.string().trim().refine((v) => v === "" || isHexColor(v), { message: "Enter a valid hex colour (e.g. #3b82f6)" });
var FieldOverridesDraftSchema = z.object({
  name: z.string().trim().max(120, "Name is too long").optional(),
  description: z.string().trim().max(2e3).optional(),
  color: hexColor2.optional(),
  icon: z.string().trim().optional(),
  logo: z.string().trim().optional(),
  aliases: z.array(z.string().trim().min(1)).optional(),
  metadata: z.record(z.string(), z.any()).optional()
});
var trimmed = (v) => {
  if (v == null) return void 0;
  const t = v.trim();
  return t === "" ? void 0 : t;
};
function overridesDraftFromDto(dto) {
  if (!dto) return {};
  return {
    name: dto.name ?? void 0,
    description: dto.description ?? void 0,
    color: dto.color ?? void 0,
    icon: dto.icon ?? void 0,
    logo: dto.logo ?? void 0,
    aliases: dto.aliases ?? void 0,
    metadata: dto.metadata ?? void 0
  };
}
function overridesDraftToDto(draft) {
  return FieldOverridesSchema.parse({
    name: trimmed(draft.name) ?? null,
    description: trimmed(draft.description) ?? null,
    color: trimmed(draft.color) ?? null,
    icon: trimmed(draft.icon) ?? null,
    logo: trimmed(draft.logo) ?? null,
    aliases: draft.aliases && draft.aliases.length > 0 ? draft.aliases : null,
    metadata: draft.metadata && Object.keys(draft.metadata).length > 0 ? draft.metadata : null
  });
}
function isEmptyFieldOverrides(draft) {
  if (!draft) return true;
  return !trimmed(draft.name) && !trimmed(draft.description) && !trimmed(draft.color) && !trimmed(draft.icon) && !trimmed(draft.logo) && (!draft.aliases || draft.aliases.length === 0) && (!draft.metadata || Object.keys(draft.metadata).length === 0);
}
function applyFieldOverrides(base, override) {
  const o = override ?? {};
  const pick = (ov, fallback) => {
    const t = trimmed(ov);
    return t ?? fallback;
  };
  const aliases = o.aliases && o.aliases.length > 0 ? o.aliases : base.aliases;
  const oMeta = o.metadata ?? void 0;
  const metadata = base.metadata || oMeta ? { ...base.metadata ?? {}, ...oMeta ?? {} } : void 0;
  return {
    name: pick(o.name, base.name),
    description: pick(o.description, base.description),
    color: pick(o.color, base.color),
    icon: pick(o.icon, base.icon),
    logo: pick(o.logo, base.logo),
    aliases,
    metadata
  };
}
function diffFieldOverrides(initial, current) {
  const chains = /* @__PURE__ */ new Set([...Object.keys(initial), ...Object.keys(current)]);
  const changes = [];
  for (const chainId of chains) {
    const cur = current[chainId];
    const hadBefore = chainId in initial && !isEmptyFieldOverrides(initial[chainId]);
    if (isEmptyFieldOverrides(cur)) {
      if (hadBefore) changes.push({ chainId, value: null });
      continue;
    }
    const nextDto = overridesDraftToDto(cur);
    const prevDto = hadBefore ? overridesDraftToDto(initial[chainId]) : null;
    if (!prevDto || JSON.stringify(prevDto) !== JSON.stringify(nextDto)) {
      changes.push({ chainId, value: nextDto });
    }
  }
  return changes;
}

// src/catalog/primary.ts
var PRIMARY_KINDS = ["tag", "label", "group", "entity"];
var seg = (v) => v == null || v === "" ? TENANT_PATH_ANY : v;
var tenantPathOf = (s) => `${seg(s.customerId)}#${seg(s.orgId)}#${seg(s.tenantId)}`;
var recordTenantPath = (r) => `${seg(r.customer_id)}#${seg(r.org_id)}#${seg(r.tenant_id)}`;
var GLOBAL_PATH = `${TENANT_PATH_ANY}#${GLOBAL_TENANT}#${GLOBAL_TENANT}`;
function tenantRank(record, tenant, includeGlobal) {
  const recPath = recordTenantPath(record);
  if (tenant && recPath === tenantPathOf(tenant)) return 2;
  if (includeGlobal && recPath === GLOBAL_PATH) return 1;
  return 0;
}
function chainRank(record, chainId) {
  const recChain = record.chain_id ?? null;
  if (chainId != null && recChain === chainId) return 2;
  if (recChain == null) return 1;
  return 0;
}
function resolvePrimary(records, kind, ctx) {
  const includeGlobal = ctx.includeGlobal ?? true;
  let best;
  let bestScore = -1;
  for (const r of records) {
    if (r.catalog_kind !== kind) continue;
    const tr = tenantRank(r, ctx.tenant, includeGlobal);
    if (tr === 0) continue;
    const cr = chainRank(r, ctx.chainId);
    if (cr === 0) continue;
    const score = tr * 10 + cr;
    if (score > bestScore || score === bestScore && best !== void 0 && isNewer(r, best)) {
      best = r;
      bestScore = score;
    }
  }
  return best;
}
function isNewer(a, b) {
  const va = a.version ?? 0;
  const vb = b.version ?? 0;
  if (va !== vb) return va > vb;
  return (a.set_at ?? 0) > (b.set_at ?? 0);
}
function resolvePrimaries(records, ctx) {
  const out = {};
  for (const kind of PRIMARY_KINDS) {
    const winner = resolvePrimary(records, kind, ctx);
    if (winner) out[kind] = winner.catalog_id;
  }
  return out;
}
function applyPrimaryFlags(hits, primaryByKind) {
  const flagged = hits.map((h) => ({
    ...h,
    isPrimary: primaryByKind[h.kind] === h.catalogId
  }));
  return flagged.map((h, i) => [h, i]).sort(([a, ai], [b, bi]) => {
    const pa = a.isPrimary ? 0 : 1;
    const pb = b.isPrimary ? 0 : 1;
    return pa !== pb ? pa - pb : ai - bi;
  }).map(([h]) => h);
}

// src/precedence/tier.ts
var Tier = {
  USER_OVERRIDE: "user_override",
  USER_BASE: "user_base",
  /** Tenant scope, verified (wire value kept as `customer_override`). */
  CUSTOMER_OVERRIDE: "customer_override",
  /** Tenant scope, unverified (wire value kept as `customer_base`). */
  CUSTOMER_BASE: "customer_base",
  /** Org-wide scope, verified — below tenant, above default. */
  ORG_OVERRIDE: "org_override",
  /** Org-wide scope, unverified. */
  ORG_BASE: "org_base",
  DEFAULT: "default",
  ENTITY_INHERITED: "entity_inherited",
  PROVIDER_CANDIDATE: "provider_candidate"
};
var TIER_WEIGHTS = {
  [Tier.USER_OVERRIDE]: 70,
  [Tier.USER_BASE]: 60,
  [Tier.CUSTOMER_OVERRIDE]: 50,
  [Tier.CUSTOMER_BASE]: 40,
  // FE-only, in the gap: below tenant, above the system default. A wire weight
  // is never 36/33, because the backend has no org tier to emit one.
  [Tier.ORG_OVERRIDE]: 36,
  [Tier.ORG_BASE]: 33,
  [Tier.DEFAULT]: 30,
  [Tier.ENTITY_INHERITED]: 20,
  [Tier.PROVIDER_CANDIDATE]: 10
};
function tierWeight(tier) {
  return TIER_WEIGHTS[tier] ?? TIER_WEIGHTS[Tier.PROVIDER_CANDIDATE];
}
function mergeByPrecedence(hits, keyFn, tierFn) {
  const chosen = /* @__PURE__ */ new Map();
  for (const hit of hits) {
    const k = keyFn(hit);
    const existing = chosen.get(k);
    if (existing === void 0 || tierWeight(tierFn(hit)) > tierWeight(tierFn(existing))) {
      chosen.set(k, hit);
    }
  }
  return [...chosen.values()];
}

// src/catalog/ownership.ts
var PLATFORM_TIERS = /* @__PURE__ */ new Set([Tier.DEFAULT, Tier.ENTITY_INHERITED, Tier.PROVIDER_CANDIDATE]);
var SCOPE_LABEL = {
  user: "Your override",
  tenant: "Tenant",
  org: "Org",
  global: "Platform default"
};
var SOLO_SCOPE_LABEL = {
  user: "Your override",
  tenant: "This workspace",
  org: "This workspace",
  global: "Platform default"
};
function parseTier(v) {
  return typeof v === "string" && v in TIER_WEIGHTS ? v : void 0;
}
function firstBool(...values) {
  for (const v of values) if (typeof v === "boolean") return v;
  return void 0;
}
function isRealTenant(v) {
  const s = asString(v);
  return s != null && s !== GLOBAL_TENANT && s !== "*";
}
function isPlatformScope(v) {
  return canonicalRung(asString(v)) === "global";
}
function deriveCatalogOwnership(row) {
  const tier = parseTier(row.tier);
  let scope;
  if (isRealTenant(row.user_id)) scope = "user";
  else if (isRealTenant(row.tenant_id) || isRealTenant(row.customer_id)) scope = "tenant";
  else if (isRealTenant(row.org_id)) scope = "org";
  else scope = "global";
  const explicitDefault = firstBool(row.is_system_default, row.system_default, row.systemDefault);
  const isSystemDefault = explicitDefault != null ? explicitDefault || isPlatformScope(row.scope) : isPlatformScope(row.scope) || scope === "global" && (tier == null || PLATFORM_TIERS.has(tier));
  if (!isSystemDefault && scope === "global") scope = "tenant";
  const explicitLock = row.editable === false || row.read_only === true || row.readonly === true;
  const readOnly = explicitLock || isSystemDefault;
  return {
    scope,
    tier,
    isSystemDefault,
    readOnly,
    label: SCOPE_LABEL[scope],
    soloLabel: SOLO_SCOPE_LABEL[scope]
  };
}
function isReadOnlyCatalogRow(row) {
  return deriveCatalogOwnership(row).readOnly;
}

// src/catalog/inheritance.ts
var defaultKey = (hit) => `${hit.kind}\0${hit.catalogId}`;
function inheritEntityHits(entityHits, entityId) {
  return entityHits.map((hit) => ({
    ...hit,
    tier: Tier.ENTITY_INHERITED,
    inheritedFromEntityId: entityId
  }));
}
function resolveInheritedHits(opts) {
  const keyFn = opts.keyFn ?? defaultKey;
  const all = [...opts.direct ?? []];
  for (const source of opts.entities ?? []) {
    all.push(...inheritEntityHits(source.hits, source.entityId));
  }
  return mergeByPrecedence(all, keyFn, (hit) => hit.tier ?? Tier.PROVIDER_CANDIDATE);
}
function isInheritedHit(hit) {
  return hit.tier === Tier.ENTITY_INHERITED || hit.inheritedFromEntityId != null;
}

// src/catalog/retirement.ts
function retirementImpact(input) {
  const noun = CATALOG_ITEM_KIND_LABEL[input.kind].toLowerCase();
  if (input.loading) {
    return {
      sentence: "Counting what is attached to it\u2026",
      tone: "counting",
      approximate: false
    };
  }
  if (input.failed || input.total === null) {
    return {
      sentence: `How much is attached to this ${noun} could not be read, so this may detach more than you expect.`,
      tone: "unknown",
      approximate: false
    };
  }
  if (input.total <= 0) {
    return {
      sentence: `Nothing is attached to it, so nothing else changes.`,
      tone: "none",
      approximate: false
    };
  }
  const approximate = input.totalCapped === true;
  const count = input.total.toLocaleString();
  const rows = input.total === 1 ? "attachment" : "attachments";
  return {
    sentence: approximate ? `At least ${count} ${rows} will be detached \u2014 addresses and transactions this ${noun} is on.` : `${count} ${rows} will be detached \u2014 addresses and transactions this ${noun} is on.`,
    tone: "some",
    approximate
  };
}

// src/catalog/resourceChanges.ts
var catalogResource = (kind) => `catalog.${kind}`;
var CATALOG_PRIMARY_RESOURCE = "catalog.primary";
var CATALOG_RESOURCES = [...CATALOG_ITEM_KINDS.map(catalogResource), CATALOG_PRIMARY_RESOURCE];

export { ALL_CHAINS, CATALOG_COMMON_FIELDS, CATALOG_FIELDS, CATALOG_ITEM_KINDS, CATALOG_ITEM_KIND_LABEL, CATALOG_LIFECYCLES, CATALOG_LIFECYCLE_LABEL, CATALOG_PRIMARY_RESOURCE, CATALOG_RESOURCES, CatalogItemDraftSchema, CatalogItemKind, DEFAULT_ENTITY_TYPE, ENTITY_FIELDS, ENTITY_TYPES, ENTITY_TYPE_LABEL, FieldOverridesDraftSchema, GLOBAL_TENANT, GROUP_FIELDS, LABEL_FIELDS, PRIMARY_KINDS, TAG_FIELDS, appliesToAllChains, applyFieldOverrides, applyPrimaryFlags, assignedChains, assignmentAppliesToChain, assignmentsForChain, catalogFields, catalogIdsByKind, catalogKindOfId, catalogResource, chainsFromAssignments, deriveCatalogOwnership, diffFieldOverrides, draftToRowBody, emptyDraft, entityTypeLabel, entityTypeOptions, inheritEntityHits, isChainAgnostic, isEmptyFieldOverrides, isEntityType, isInheritedHit, isReadOnlyCatalogRow, overridesDraftFromDto, overridesDraftToDto, primaryChainsOutsideAppliesTo, resolveInheritedHits, resolvePrimaries, resolvePrimary, retirementImpact, rowToDraft, twitterLink, validateDraft, webUrl, webUrlLabel };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map